import { useCallback, useEffect, useImperativeHandle, useRef, useState, type RefObject } from 'react';
import { storyAssetUrl, storyDuration, storyPosition, type StoryDocument } from '../shared/story';
import { useDialogFocus } from './useDialogFocus';

export type StoryController = { start: () => void };
export function StoryPlayer({
  document: doc,
  onClose,
  controller,
}: {
  document: StoryDocument;
  onClose: () => void;
  controller: RefObject<StoryController | null>;
}) {
  const root = useRef<HTMLDivElement>(null);
  const video = useRef<HTMLVideoElement>(null);
  const audio = useRef<HTMLAudioElement>(null);
  const [time, setTime] = useState(0);
  const timeRef = useRef(0);
  const [started, setStarted] = useState(false);
  const [paused, setPaused] = useState(false);
  const [held, setHeld] = useState(false);
  const [muted, setMuted] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [failure, setFailure] = useState('');
  const [imageReady, setImageReady] = useState(false);
  const pointer = useRef<{ x: number; y: number; at: number; pointerId: number } | null>(null);
  const duration = storyDuration(doc);
  const isVideo = doc.settings.mode === 'video';
  const position = storyPosition(doc.settings.slides, time);
  const slide = doc.settings.slides[position.index];
  const ended = time >= duration;
  const playing = started && !paused && !held && !blocked && !failure && !ended;
  const tracks = isVideo && !doc.settings.musicOnVideo ? [] : doc.settings.tracks;
  const latestTrack = [...tracks].reverse().find((track) => time >= track.startSeconds);
  const latestAsset = doc.assets.find((asset) => asset.id === latestTrack?.assetId);
  const selectedTrack =
    latestTrack &&
    latestAsset &&
    time < latestTrack.startSeconds + latestAsset.duration - latestTrack.offsetSeconds
      ? latestTrack
      : undefined;
  const firstTrack = tracks[0];
  const track = selectedTrack ?? firstTrack;
  const trackRef = useRef(selectedTrack);
  useEffect(() => {
    trackRef.current = selectedTrack;
  }, [selectedTrack]);
  const close = useCallback(() => {
    audio.current?.pause();
    video.current?.pause();
    if (document.fullscreenElement === root.current) void document.exitFullscreen().catch(() => {});
    screen.orientation?.unlock?.();
    onClose();
  }, [onClose]);
  useDialogFocus(true, root, close);
  const fullscreen = useCallback(() => {
    const element = root.current;
    if (element?.requestFullscreen) {
      void element
        .requestFullscreen()
        .then(() => {
          const orientation = screen.orientation as ScreenOrientation & {
            lock?: (value: string) => Promise<void>;
          };
          void orientation?.lock?.('landscape').catch(() => {});
        })
        .catch(() => {});
    } else if (video.current && 'webkitEnterFullscreen' in video.current) {
      (video.current as HTMLVideoElement & { webkitEnterFullscreen: () => void }).webkitEnterFullscreen();
    }
  }, []);
  const begin = useCallback(() => {
    setStarted(true);
    setPaused(false);
    setBlocked(false);
    if (video.current) void video.current.play().catch(() => setBlocked(true));
    if (audio.current && trackRef.current) void audio.current.play().catch(() => setBlocked(true));
  }, []);
  useImperativeHandle(
    controller,
    () => ({
      start() {
        begin();
        fullscreen();
      },
    }),
    [begin, fullscreen],
  );
  useEffect(() => {
    const element = video.current;
    if (!element) return;
    element.muted = muted || !doc.settings.videoSound;
    if (playing) void element.play().catch(() => setBlocked(true));
    else element.pause();
  }, [playing, muted, doc.settings.videoSound]);
  useEffect(() => {
    const element = audio.current;
    if (!element) return;
    element.muted = muted;
    if (!selectedTrack || !playing) {
      element.pause();
      return;
    }
    element.volume = selectedTrack.volume;
    const desired = time - selectedTrack.startSeconds + selectedTrack.offsetSeconds;
    if (Number.isFinite(element.duration) && Math.abs(element.currentTime - desired) > 0.7)
      element.currentTime = desired;
    if (element.paused) void element.play().catch(() => setBlocked(true));
  }, [time, playing, muted, selectedTrack]);
  useEffect(() => {
    if (isVideo || !playing || !imageReady) return;
    let frame = 0;
    let previous = performance.now();
    const tick = (now: number) => {
      const next = Math.min(duration, timeRef.current + Math.min(0.1, (now - previous) / 1000));
      previous = now;
      timeRef.current = next;
      setTime(next);
      if (next < duration) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [playing, imageReady, isVideo, duration]);
  useEffect(() => {
    const visibility = () => {
      if (document.hidden) {
        setPaused(true);
        setHeld(false);
        pointer.current = null;
      }
    };
    document.addEventListener('visibilitychange', visibility);
    return () => document.removeEventListener('visibilitychange', visibility);
  }, []);
  useEffect(
    () => () => {
      screen.orientation?.unlock?.();
    },
    [],
  );
  const seek = useCallback(
    (value: number) => {
      const next = Math.max(0, Math.min(duration, value));
      timeRef.current = next;
      setTime(next);
      if (video.current) video.current.currentTime = next;
      const song = trackRef.current;
      if (audio.current && song)
        audio.current.currentTime = Math.max(0, next - song.startSeconds + song.offsetSeconds);
    },
    [duration],
  );
  const move = useCallback(
    (direction: number) => {
      if (isVideo) {
        seek(timeRef.current + direction * 10);
        return;
      }
      const current = storyPosition(doc.settings.slides, timeRef.current);
      const nextIndex = Math.max(0, Math.min(doc.settings.slides.length, current.index + direction));
      seek(doc.settings.slides.slice(0, nextIndex).reduce((total, item) => total + item.seconds, 0));
    },
    [isVideo, doc.settings.slides, seek],
  );
  useEffect(() => {
    const key = (event: KeyboardEvent) => {
      if ((event.target as Element).closest('input')) return;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        move(event.key === 'ArrowLeft' ? -1 : 1);
      }
      if (event.code === 'Space' && event.target === root.current) {
        event.preventDefault();
        setPaused((value) => !value);
      }
    };
    document.addEventListener('keydown', key);
    return () => document.removeEventListener('keydown', key);
  }, [move]);
  const formatTime = (seconds: number) =>
    `${Math.floor(seconds / 60)}:${String(Math.floor(seconds % 60)).padStart(2, '0')}`;
  return (
    <div
      className="lloyd-story-player"
      role="dialog"
      aria-modal="true"
      aria-label="Lloyd’s story"
      ref={root}
      tabIndex={-1}
    >
      <div className="story-player-top">
        <div className="story-progress" aria-label="Story progress">
          {isVideo ? (
            <progress max={duration || 1} value={time} />
          ) : (
            doc.settings.slides.map((item, index) => (
              <progress
                key={`${item.assetId}-${index}`}
                max={1}
                value={index < position.index ? 1 : index === position.index ? position.progress : 0}
              />
            ))
          )}
        </div>
        <div className="story-player-title">
          <strong>Lloyd’s story</strong>
          <button onClick={close} aria-label="Close story">
            Close ×
          </button>
        </div>
      </div>
      <div
        className="story-player-stage"
        onPointerDown={(event) => {
          if (!event.isPrimary || event.button !== 0) return;
          pointer.current = {
            x: event.clientX,
            y: event.clientY,
            at: performance.now(),
            pointerId: event.pointerId,
          };
          event.currentTarget.setPointerCapture(event.pointerId);
          setHeld(true);
        }}
        onPointerCancel={() => {
          pointer.current = null;
          setHeld(false);
        }}
        onLostPointerCapture={() => {
          pointer.current = null;
          setHeld(false);
        }}
        onPointerUp={(event) => {
          const start = pointer.current;
          pointer.current = null;
          setHeld(false);
          if (!start || start.pointerId !== event.pointerId) return;
          const dx = event.clientX - start.x,
            dy = event.clientY - start.y;
          if (dy > 70 && Math.abs(dy) > Math.abs(dx) * 1.5) {
            close();
            return;
          }
          if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) {
            move(dx < 0 ? 1 : -1);
            return;
          }
          if (performance.now() - start.at > 250 || Math.max(Math.abs(dx), Math.abs(dy)) > 12) return;
          const bounds = event.currentTarget.getBoundingClientRect();
          move(event.clientX - bounds.left < bounds.width / 3 ? -1 : 1);
        }}
      >
        {isVideo ? (
          <video
            ref={video}
            src={doc.settings.videoId ? storyAssetUrl(doc.settings.videoId) : undefined}
            playsInline
            preload="auto"
            onTimeUpdate={(event) => {
              timeRef.current = event.currentTarget.currentTime;
              setTime(timeRef.current);
            }}
            onEnded={() => {
              timeRef.current = duration;
              setTime(duration);
            }}
            onError={() =>
              setFailure('This video could not play. Check your connection or try the MP4 on another device.')
            }
          />
        ) : (
          slide && (
            <img
              key={slide.assetId}
              src={storyAssetUrl(slide.assetId)}
              alt={`Lloyd’s story slide ${position.index + 1}`}
              ref={(image) => {
                if (image) setImageReady(image.complete && image.naturalWidth > 0);
              }}
              onLoad={() => setImageReady(true)}
              onError={() => {
                setImageReady(false);
                setFailure('This slide could not load. Check your connection and reopen the story.');
              }}
              draggable={false}
            />
          )
        )}
      </div>
      {track && (
        <audio
          ref={audio}
          src={storyAssetUrl(track.assetId)}
          preload="auto"
          onError={() => setFailure('This song could not play. Try reopening the story.')}
        />
      )}
      {(!started || blocked || ended || failure) && (
        <div className="story-player-message">
          <p>
            {failure ||
              (ended
                ? 'Thank you for sharing Lloyd’s story.'
                : blocked
                  ? 'Tap to start playback with sound.'
                  : 'Lloyd’s story is ready.')}
          </p>
          {!failure && (
            <button
              onClick={() => {
                if (ended) seek(0);
                begin();
                fullscreen();
              }}
            >
              {ended ? 'Watch again' : 'Play story'}
            </button>
          )}
          {failure && <button onClick={close}>Back to the album</button>}
        </div>
      )}
      <div className="story-player-bottom">
        <div className="story-player-controls">
          <button aria-label={isVideo ? 'Back 10 seconds' : 'Previous slide'} onClick={() => move(-1)}>
            ‹
          </button>
          <button
            onClick={() => {
              if (!started || blocked) begin();
              else setPaused((value) => !value);
            }}
            aria-label={playing ? 'Pause story' : 'Play story'}
          >
            {playing ? 'Pause' : 'Play'}
          </button>
          <button aria-label={isVideo ? 'Forward 10 seconds' : 'Next slide'} onClick={() => move(1)}>
            ›
          </button>
          <button
            onClick={() => setMuted((value) => !value)}
            aria-label={muted ? 'Unmute story' : 'Mute story'}
          >
            {muted ? 'Sound off' : 'Sound on'}
          </button>
          <button onClick={fullscreen} aria-label="Full screen">
            Full screen
          </button>
        </div>
        {isVideo ? (
          <label className="story-seek">
            {formatTime(time)}
            <input
              type="range"
              aria-label="Video position"
              min={0}
              max={duration}
              step={0.1}
              value={time}
              onChange={(event) => seek(Number(event.target.value))}
            />
            {formatTime(duration)}
          </label>
        ) : (
          <p className="story-player-instruction">
            {position.index + 1} / {doc.settings.slides.length} · Tap either side to skip · Hold to pause
          </p>
        )}
        <p className="story-rotate-hint">Turn your phone sideways for the landscape view.</p>
      </div>
    </div>
  );
}
